import { aptGenerationApi } from '@/api/apt_generation.api';
import { Maximize, Minimize } from 'lucide-react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ToolpathDialogProps {
  visible: boolean;
  title: string;
  pocketProcedureId: string | null;
  onClose: () => void;
}

/**
 * Modal showing an interactive Plotly toolpath visualization inside an iframe.
 *
 * The iframe loads HTML produced server-side by `plot_toolpath` (one trace per
 * `.h` file).  We fetch the HTML through the authenticated apiClient as a
 * Blob, wrap it in `URL.createObjectURL`, and feed that to the iframe — a
 * plain `<iframe src="/api/v1/pocket-procedure/.../toolpath.html">` would 401
 * because iframes don't forward the parent app's Bearer token.
 */
export const ToolpathDialog: FC<ToolpathDialogProps> = ({ visible, title, pocketProcedureId, onClose }) => {
  const { t } = useTranslation();
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    if (!visible || !pocketProcedureId) {
      return;
    }
    let cancelled = false;
    let activeUrl: string | null = null;
    setIsLoading(true);
    setError(null);
    setIframeUrl(null);
    aptGenerationApi
      .getPocketProcedureToolpathHtml(pocketProcedureId)
      .then((blob) => {
        if (cancelled) return;
        // Force text/html so the iframe renders the page instead of treating
        // it as a generic octet-stream when the blob type isn't set by axios.
        const htmlBlob = blob.type === 'text/html' ? blob : new Blob([blob], { type: 'text/html' });
        activeUrl = URL.createObjectURL(htmlBlob);
        setIframeUrl(activeUrl);
      })
      .catch(() => {
        if (!cancelled) setError(t('projectDetail.toolpathDialogError'));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
      if (activeUrl) URL.revokeObjectURL(activeUrl);
    };
  }, [visible, pocketProcedureId, t]);

  // Revoke the current object URL whenever the dialog closes — covers the
  // case where `visible` flips to false before the fetch resolves.
  useEffect(() => {
    if (!visible && iframeUrl) {
      URL.revokeObjectURL(iframeUrl);
      setIframeUrl(null);
    }
  }, [visible, iframeUrl]);

  const dialogHeader = (
    <div className="flex items-center justify-between w-full pr-2">
      <span className="font-semibold text-lg">{title}</span>
      <Button
        icon={isFullscreen ? <Minimize size={16} /> : <Maximize size={16} />}
        onClick={() => setIsFullscreen((prev) => !prev)}
        text={true}
        rounded={true}
        size="small"
        title={isFullscreen ? t('dialogs.exitFullscreen') : t('dialogs.enterFullscreen')}
        tooltip={isFullscreen ? t('dialogs.exitFullscreen') : t('dialogs.enterFullscreen')}
        tooltipOptions={{ position: 'left' }}
        aria-label={isFullscreen ? t('dialogs.exitFullscreen') : t('dialogs.enterFullscreen')}
        pt={{
          root: {
            style: { outline: 'none', boxShadow: 'none' },
            className: 'focus:!ring-0 focus:!outline-none focus-visible:!ring-0 focus-visible:!outline-none',
          },
        }}
      />
    </div>
  );

  return (
    <Dialog
      header={dialogHeader}
      visible={visible}
      onHide={onClose}
      modal={true}
      className={`w-full ${isFullscreen ? 'max-w-full h-full' : 'max-w-5xl'}`}
      dismissableMask={true}
      style={
        isFullscreen ? { width: '100vw', height: '100vh', maxWidth: '100vw', maxHeight: '100vh', margin: 0 } : undefined
      }
      pt={{
        root: { className: isFullscreen ? 'h-full fixed inset-0' : '' },
        content: { className: isFullscreen ? 'h-full flex flex-col' : '' },
      }}
    >
      <div className={`flex flex-col ${isFullscreen ? 'h-full' : ''}`}>
        {isLoading ? (
          <div className="flex items-center justify-center py-12 text-gray-600">
            {t('projectDetail.toolpathDialogLoading')}
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-12 text-red-600">{error}</div>
        ) : iframeUrl ? (
          <iframe
            title={title}
            src={iframeUrl}
            // Plotly needs scripts; same-origin lets the modebar / hover work.
            sandbox="allow-scripts allow-same-origin"
            className={`w-full border-0 bg-white ${isFullscreen ? 'flex-1' : ''}`}
            style={isFullscreen ? undefined : { height: '70vh' }}
          />
        ) : null}
      </div>
    </Dialog>
  );
};
